import { useHomeContactAnchor } from 'components/ContactNavigation';
import HeroOverlay from 'components/HeroOverlay';
import './HomeHero.css';
import PageStoryImage from 'components/PageStoryImage';
import PageIntroBand from 'components/PageIntroBand';
import TmsFeatureSection from 'components/home/TmsFeatureSection';
import { speakerDisplayName } from 'lib/speakerPresentation';
import { resolveCoursePresentation } from 'lib/coursePresentation';
import 'components/CoursePresentation.css';
import { useServices } from 'hooks/useServices';
import { useHeroScrollMotion } from 'hooks/useHeroScrollMotion';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight, BookOpen, CheckCircle2, Clock, FileText, Loader2, Play, Quote, UserRound, Video, X } from 'lucide-react';
import SiteHeroCtas from 'components/SiteHeroCtas';
import { useHomeSpotlights } from 'components/HomeInteractionEffects';
import { FlowButton } from 'components/ui/flow-button';
import { TestimonialsSection } from 'components/blocks/testimonials-with-marquee';
import { PaperworkPackageCarousel, SpotlightComparisonTable } from 'components/paperwork/SpotlightComparisonTable';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { listPublicSpeakers, contentOrder } from 'lib/siteSpeakersApi';
import { listPublicCourses } from 'lib/siteCoursesApi';
import { extractCarrierFlowReference, submitWebsiteContact } from 'lib/sitePublicApi';
import { formatBlogDate, listPublishedBlogPosts } from 'lib/blogCms';
import { splitSiteContentParagraphs } from 'lib/siteContentCms';
import { useSiteContent } from 'hooks/useSiteContent';
import { toast } from 'sonner';
import { usePackagePriceDetails, usePackagePriceStatus } from 'lib/paperworkPrices';
import { formatCartMoney } from 'lib/paperworkCart';
import {
  CourseThumb,
  GridGlow,
} from 'components/MagellanPrototypePrimitives';
import {
  SPEAKER_FALLBACK_IMAGE,
  WEBINAR_FALLBACK_IMAGE,
  getInitials as getDisplayInitials,
  getWebinarDetailPath,
  slugifyPublic,
} from 'lib/magellanDisplayData';

const RESERVE_IMAGE = '/images/companies/company-hero-desk.png';
const SPEAKER_CARD_POSTERS = [
  SPEAKER_FALLBACK_IMAGE,
  '/images/webinars/webinars-hero-desk.png',
  '/images/resources/resources-hero-documents.png',
];
const EASE = [0.16, 1, 0.3, 1];

const BTN_PRIMARY =
  'group/btn inline-flex items-center justify-center gap-2 font-body text-[11px] font-bold uppercase tracking-[0.22em] px-7 py-4';
const BTN_GHOST = BTN_PRIMARY;

const MARQUEE_ITEMS = [
  'Company paperwork',
  'Driver files',
  'Safety and ELD staffing',
  'Recorded courses',
  'Direct-hire recruitment',
  'Monthly reviews',
  'CarrierFlow TMS',
  'Management visibility',
];

const PAPERWORK_PACKAGES = [
  {
    id: 'basic',
    documentCount: 67,
    title: 'Basic',
    saleLabel: 'Founding',
    anchor: '/paperwork?package=basic',
    ctaLabel: 'View Basic',
    line: 'Policies, procedures, forms and tracking workbooks for everyday carrier operations. Review, written findings and a meeting are included. Company details, logo and small edits up to two hours are an optional paid add-on.',
    items: ['Daily operating policies and records', 'Forms and tracking workbooks', 'Written setup guidance'],
  },
  {
    id: 'standard',
    documentCount: 150,
    title: 'Standard',
    saleLabel: 'Founding',
    anchor: '/paperwork?package=standard',
    ctaLabel: 'View Standard',
    line: 'Basic paperwork plus ISO 9001 quality management documentation and additional procedures. Specialist paperwork is agreed after review, with a review deposit credited toward the package price.',
    items: ['ISO 9001 quality management system', 'Expanded policies and procedures', 'Specialist paperwork agreed after review'],
  },
  {
    id: 'premium',
    documentCount: 270,
    title: 'Premium',
    saleLabel: '',
    anchor: '/paperwork?package=premium',
    ctaLabel: 'View Premium',
    line: 'Standard paperwork plus ISO 14001 environmental and ISO 45001 occupational health and safety management documentation, with expanded policies and procedures. Specialist freight paperwork is agreed after review.',
    items: ['Quality, environmental, health and safety documentation', 'Expanded policies and procedures', 'Specialist freight paperwork agreed after review'],
  },
];

const FAQS = [
  {
    q: 'What does Magellan International offer?',
    a: 'Company paperwork packages, recorded courses, office staffing and recruitment, monthly reviews, and CarrierFlow TMS for US trucking companies. Each has its own scope and purchase terms. Buying one does not automatically include the others.',
  },
  {
    q: 'What if I do not know which paperwork package fits?',
    a: 'Start on the Paperwork page to compare Basic, Standard and Premium. You can complete the free quick assessment or request the optional introductory meeting. Every package includes a review of your existing paperwork after payment is confirmed. We send written findings before meeting with you to discuss what is needed.',
  },
  {
    q: 'Can I buy a course without a paperwork package?',
    a: 'Yes. Choose one recorded course for $299 per person or the current ten-course series for $1,499 per person. Both include videos and accompanying learning material, with three months of access starting when Magellan receives payment. A course purchase does not include a paperwork package or a TMS subscription.',
  },
  {
    q: 'Is this legal or regulatory advice?',
    a: 'No. Magellan provides operational documentation systems, training materials, and implementation support. Legal, regulatory, insurance, and compliance decisions should be reviewed with qualified professionals.',
  },
];

function sanitizeText(value) {
  return String(value || '').trim();
}

function hexToRgba(hex, alpha = 1) {
  const normalized = sanitizeText(hex).replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(normalized)) return `rgba(76, 141, 204, ${alpha})`;
  const value = Number.parseInt(normalized, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function scrollToSection(id) {
  const element = document.getElementById(id);
  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function useMotionPrefs() {
  const reduced = useReducedMotion();
  const motionOn = typeof window !== 'undefined' && !reduced;
  const fadeUp = motionOn
    ? {
        initial: { opacity: 0, y: 34, filter: 'blur(8px)' },
        whileInView: { opacity: 1, y: 0, filter: 'blur(0px)' },
        viewport: { once: true, amount: 0.25 },
        transition: { duration: 0.85, ease: EASE },
      }
    : {};
  const stagger = motionOn
    ? {
        initial: 'hidden',
        whileInView: 'show',
        viewport: { once: true, amount: 0.18 },
        variants: { hidden: {}, show: { transition: { staggerChildren: 0.11 } } },
      }
    : {};
  const item = motionOn
    ? {
        variants: {
          hidden: { opacity: 0, y: 34, filter: 'blur(8px)' },
          show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.75, ease: EASE } },
        },
      }
    : {};
  return { motionOn, fadeUp, stagger, item };
}

function HomeSectionLabel({ index, children, className = '', accentColor }) {
  const label = sanitizeText(children).replace(/^\(\s*\d+\s*\)\s*[—–-]?\s*/, '');
  return (
    <div data-home-section-index={index} className={`flex items-center gap-3 font-body text-[10px] uppercase tracking-[0.3em] ${className}`}>
      <span className="text-accent-ink" style={accentColor ? { color: accentColor } : undefined}>( {index} )</span>
      <span aria-hidden="true" className="h-px w-8 bg-gold/35" style={accentColor ? { backgroundColor: accentColor } : undefined} />
      <span className="text-copy/45">{label}</span>
    </div>
  );
}

function SectionIntro({ index, eyebrow, title, copy, align = 'left' }) {
  const { fadeUp } = useMotionPrefs();
  return (
    <motion.div
      className={`mb-14 md:mb-18 ${align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}`}
      {...fadeUp}
    >
      <HomeSectionLabel index={index.replace(/\D/g, '')} className={`mb-5 ${align === 'center' ? 'justify-center' : ''}`}>{eyebrow}</HomeSectionLabel>
      <h2 className="font-display text-4xl font-light leading-[0.98] tracking-tight text-ink sm:text-5xl lg:text-6xl">
        {title}
      </h2>
      {copy ? <p className={`mt-6 font-body text-base leading-8 text-copy/48 ${align === 'center' ? 'mx-auto max-w-2xl' : 'max-w-xl'}`}>{copy}</p> : null}
    </motion.div>
  );
}

function formatWebinarSchedule(webinar) {
  const value = webinar?.startAt || webinar?.date;
  if (!value) return sanitizeText(webinar?.time) || 'Schedule to be announced';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return sanitizeText(webinar?.time) || String(value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(webinar?.timeZone ? { timeZone: webinar.timeZone, timeZoneName: 'short' } : {}),
  }).format(date);
}

function speakerKey(speaker) {
  return sanitizeText(speaker?.profileSlug || speaker?.name || speaker?.headshotUrl).toLowerCase();
}

function getSpeakerCardPoster(speaker, index) {
  return sanitizeText(speaker?.headshotUrl || speaker?.posterUrl || speaker?.imageUrl)
    || SPEAKER_CARD_POSTERS[index % SPEAKER_CARD_POSTERS.length];
}

function getSpeakerCardQuote(speaker) {
  return sanitizeText(speaker?.quote || speaker?.headline || speaker?.shortBio || speaker?.bio);
}

// Local review copy only: keep published CMS records, media and CTA destinations intact.
const HOME_COPY_PREVIEW = {
  homeHero: {
    primaryCtaLabel: 'View paperwork packages',
    primaryCtaPath: '/paperwork#paperwork-hero',
    secondaryCtaLabel: 'Explore CarrierFlow TMS',
    secondaryCtaPath: '/tms',
    tertiaryCtaLabel: '',
    tertiaryCtaPath: '',
    title: 'Build a company your team can run.',
    body: [
      'You hired people to help. Too many routine questions still come back to you.',
      'Magellan International helps US trucking companies organize the office behind the fleet through company paperwork, practical courses, staffing and CarrierFlow TMS.',
      'A new hire needs to know what to check, which decisions they can make and when another department needs to take over.',
      'Written procedures give people a reference. Training helps them use it, staffing adds capacity, and software keeps information with the task. Choose the support your company needs.',
    ].join('\n\n'),
  },
  homeSpeakers: {
    eyebrow: 'Course speakers',
    title: 'Meet the people teaching the work.',
    body: "Read each speaker's background and teaching topics to see how their experience relates to the responsibilities you want to learn.",
  },
  homeMasterclass: {
    eyebrow: 'Courses',
    title: 'Learn the office work behind every load.',
    body: 'Choose a recorded course on driver records, onboarding, Safety, recruiting, dispatch handoffs or management. Each course costs $299 per person; the current ten-course series costs $1,499 per person. Recorded videos and learning material are included. Access lasts three months from receipt of payment.',
  },
  homePathways: {
    eyebrow: 'Services and resources',
    title: 'Find the people and support your office needs.',
    body: "Arrange Safety or ELD staff through Magellan, recruit someone your carrier hires directly, or set up monthly reviews. Explore the existing service and resource options below to find the support that fits your company.",
  },
};

function useHomeContent(key) {
  const content = useSiteContent(key);
  const isLocalPreview = process.env.NODE_ENV === 'development'
    && typeof window !== 'undefined'
    && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  return isLocalPreview ? { ...content, ...HOME_COPY_PREVIEW[key] } : content;
}

function HomeHero() {
  const hero = { ...useHomeContent('homeHero'), primaryCtaLabel: 'View paperwork packages', primaryCtaPath: '/paperwork#paperwork-hero', secondaryCtaLabel: 'Explore CarrierFlow TMS', secondaryCtaPath: '/tms', tertiaryCtaLabel: '', tertiaryCtaPath: '' };
  const paragraphs = splitSiteContentParagraphs(hero.body);
  const heroRef = useRef(null);
  const { motionOn } = useMotionPrefs();
  const heroScrollMotion = useHeroScrollMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.22]);
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '52%']);
  const textOpacity = useTransform(scrollYProgress, [0, 0.72], [1, 0]);
  const titleWords = sanitizeText(hero.title || 'Build a company your team can run.').split(/\s+/);
  const titleGoldStartIndex = Math.max(0, titleWords.length - 4);
  const regularTitleWords = titleWords.slice(0, titleGoldStartIndex);
  const goldTitlePhrase = titleWords.slice(titleGoldStartIndex).join(' ');

  return (
    <section
      id="hero"
      ref={heroRef}
      data-testid="home-hero" data-theme-surface="dark"
      className="relative min-h-[100svh] w-full overflow-hidden md:h-screen md:min-h-[680px]"
    >
      <motion.div
        className={`absolute inset-0 ${heroScrollMotion ? 'will-change-transform' : ''}`}
        style={heroScrollMotion ? { scale: bgScale, y: bgY } : { scale: 1.02, y: 0 }}
      >
        {hero.imageUrl && <img
          src={hero.imageUrl}
          alt={hero.imageAlt || 'Magellan trucking operations'}
          className="h-full w-full object-cover opacity-70"
        />}
      </motion.div>
      <HeroOverlay />
      <div className="absolute inset-0 grain-overlay" />

      <motion.div
        className="home-hero-content relative z-10 flex h-full flex-col justify-center px-6 pt-16 text-center md:px-12"
        style={heroScrollMotion ? { y: textY, opacity: textOpacity } : { y: 0, opacity: 1 }}
      >
        <h1
          aria-label={hero.title}
          className="mx-auto max-w-5xl font-display text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl xl:text-[6rem]"
        >
          {regularTitleWords.map((word, index) => (
            <span key={`${word}-${index}`} aria-hidden="true" className="mr-4 inline-block overflow-hidden align-bottom">
              <motion.span
                className="inline-block"
                initial={motionOn ? { y: '112%' } : false}
                animate={motionOn ? { y: '0%' } : undefined}
                transition={{ delay: 0.38 + index * 0.055, duration: 0.9, ease: EASE }}
              >
                {word}
              </motion.span>
            </span>
          ))}
          <span aria-hidden="true" className="block italic shimmer-text">
            <motion.span
              className="inline"
              initial={motionOn ? { opacity: 0 } : false}
              animate={motionOn ? { opacity: 1 } : undefined}
              transition={{ delay: 0.38 + regularTitleWords.length * 0.055, duration: 0.9, ease: EASE }}
            >
              {goldTitlePhrase}
            </motion.span>
          </span>
        </h1>
        <motion.div
          initial={motionOn ? { opacity: 0, y: 24 } : false}
          animate={motionOn ? { opacity: 1, y: 0 } : undefined}
          transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
          className="mx-auto mt-10 max-w-2xl space-y-3 font-body text-base leading-8 text-copy/62"
        >
          {paragraphs.slice(0, 2).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </motion.div>
        <motion.div
          initial={motionOn ? { opacity: 0, y: 24 } : false}
          animate={motionOn ? { opacity: 1, y: 0 } : undefined}
          transition={{ delay: 1.22, duration: 0.9, ease: EASE }}
          className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <SiteHeroCtas
            content={hero}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row"
            actionComponent={FlowButton}
            primaryClassName={BTN_PRIMARY}
            secondaryClassName={BTN_GHOST}
            tertiaryClassName={BTN_GHOST}
          />
        </motion.div>
      </motion.div>

      <motion.button
        type="button"
        onClick={() => scrollToSection('speakers')}
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 md:bottom-8"
        initial={motionOn ? { opacity: 0 } : false}
        animate={motionOn ? { opacity: 1 } : undefined}
        transition={{ delay: 1.65, duration: 0.8 }}
      >
        <span className="font-body text-[10px] uppercase tracking-[0.32em] text-copy/38">Scroll</span>
        <motion.span
          className="h-8 w-px bg-gradient-to-b from-gold/70 to-transparent"
          animate={motionOn ? { scaleY: [0.45, 1, 0.45], opacity: [0.45, 1, 0.45] } : undefined}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformOrigin: 'top' }}
        />
      </motion.button>
    </section>
  );
}

function HomeIntroBand() {
  return (
    <PageIntroBand
      testId="home-page-intro"
      label="The foundation"
      title="Help your team handle the daily work."
      backgroundText="MAGELLAN"
      paragraphs={[
        "A new hire needs to know what to check, which decisions they can make and when another department needs to take over.",
        "Written procedures give people a reference. Training helps them use it, staffing adds capacity, and software keeps information with the task. Choose the support your company needs.",
      ]}
    />
  );
}

// Original brand statements, attributed to Magellan; these are not customer endorsements.
const MAGELLAN_QUOTES = [
  { author: { name: 'Magellan', handle: 'Company operations' }, text: 'Clear responsibilities give people room to do the work—and give the owner room to manage.' },
  { author: { name: 'Magellan', handle: 'Employee training' }, text: 'Training should prepare people for the decisions they face during a normal working day.' },
  { author: { name: 'Magellan', handle: 'Company paperwork' }, text: 'Company paperwork should help your team find the record, understand the procedure and take the next step.' },
  { author: { name: 'Magellan', handle: 'CarrierFlow TMS' }, text: 'Software earns its place by giving the business time back.' },
  { author: { name: 'Magellan', handle: 'Management visibility' }, text: 'Follow-up works when the next action belongs to someone.' },
  { author: { name: 'Magellan', handle: 'Operating knowledge' }, text: 'An organized company keeps knowledge in its processes, where the whole team can use it.' },
];

function HomeTestimonialsSection() {
  return <TestimonialsSection
    eyebrow={<HomeSectionLabel index="06">From Magellan</HomeSectionLabel>}
    title="How we approach trucking operations."
    description="What we look for in useful procedures, practical training and software that supports the people doing the job."
    testimonials={MAGELLAN_QUOTES}
  />;
}

function HomeOperatingTextSection() {
  const { stagger, item } = useMotionPrefs();
  const stages = [
    { number: '01', label: 'Knowledge', title: 'Teach the decisions behind the task.', copy: 'Show people how to check a record, recognize a problem and use the company procedure when a question arises.', points: ['Written procedures to refer to', 'Practice with everyday office tasks'] },
    { number: '02', label: 'Responsibility', title: 'Define the role.', copy: 'Explain who requests missing information, who checks it and when the matter needs a manager\'s decision.', points: ['Duties assigned to the right person', 'A clear point for escalation'] },
    { number: '03', label: 'Follow-through', title: 'Make handoffs easier to follow.', copy: 'Keep the documents and current status with the task so the next person can continue without starting the search again.', points: ['Information passed between departments', 'Unfinished tasks visible to management'] },
  ];
  return (
    <section data-testid="home-operating-text" className="relative overflow-hidden bg-canvas py-16 md:py-20">
      <GridGlow />
      <div className="site-content-shell relative">
        <motion.div className="mb-8 grid gap-5 lg:grid-cols-[1.35fr_0.65fr] lg:items-end lg:gap-12" {...item}>
          <div>
            <HomeSectionLabel index="00" className="mb-4">Company operations</HomeSectionLabel>
            <h2 className="max-w-3xl font-display text-4xl font-light leading-[1.02] tracking-tight text-ink sm:text-5xl">Give each role a way to finish the job.</h2>
          </div>
          <p className="max-w-xl font-body text-sm leading-7 text-copy/55">Start with what the person must know, what they are responsible for and how the next department receives the work.</p>
        </motion.div>
        <PageStoryImage testId="home-operations-image" src="/images/about/truck-road.jpg" alt="A truck travelling along an open highway" position="center 62%" />
        <motion.div className="grid grid-cols-1 gap-4 md:grid-cols-3" {...stagger}>
          {stages.map(stage => (
            <motion.article key={stage.number} className="home-spotlight group flex h-full flex-col rounded-xl border border-gold/15 bg-surface p-5 transition-colors hover:border-gold/40 lg:p-6" {...item}>
              <div className="mb-4 flex items-center justify-between gap-3">
                <p className="font-body text-[10px] uppercase tracking-[0.25em] text-accent-ink">{stage.label}</p>
                <span className="font-display text-2xl leading-none text-accent-ink/40">{stage.number}</span>
              </div>
              <h3 className="font-display text-2xl leading-tight text-ink">{stage.title}</h3>
              <p className="mb-5 mt-3 font-body text-sm leading-6 text-copy/55">{stage.copy}</p>
              <ul className="mt-auto space-y-2 border-t border-ink/8 pt-4">
                {stage.points.map(point => <li key={point} className="flex items-start gap-2.5 font-body text-xs leading-5 text-copy/60"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-ink/70" strokeWidth={1.5} />{point}</li>)}
              </ul>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function SpeakerCard({ speaker, index }) {
  const profilePath = `/speakers/${speaker.profileSlug || slugifyPublic(speaker.name, `speaker-${index + 1}`)}`;
  const poster = getSpeakerCardPoster(speaker, index);
  const quote = getSpeakerCardQuote(speaker);
  const role = [speaker.title, speaker.company].filter(Boolean).join(', ') || speaker.location || 'Magellan course speaker';
  const hasHeadshot = Boolean(sanitizeText(speaker.headshotUrl));

  return (
    <Link
      to={profilePath}
      data-testid={`home-speaker-card-${index}`} data-theme-surface="dark"
      aria-label={`View ${speakerDisplayName(speaker) || 'speaker'} profile`}
      className="home-spotlight group relative block aspect-[3/4] w-full overflow-hidden rounded-xl text-left cursor-pointer"
    >
      <img
        src={poster}
        alt={speaker.portraitAlt || speakerDisplayName(speaker) || 'Magellan course speaker'}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
      />
      {!hasHeadshot ? (
        <div className="absolute left-6 top-6 grid h-14 w-14 place-items-center rounded-full border border-gold/45 bg-black/45 font-display text-lg text-accent-ink shadow-[0_18px_42px_rgba(0,0,0,0.35)] backdrop-blur-md">
          {getDisplayInitials(speakerDisplayName(speaker))}
        </div>
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10 transition-opacity duration-500 group-hover:from-black/90" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="grid h-16 w-16 place-items-center rounded-full border border-ink/40 bg-ink/5 text-ink backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:border-[#4c8dcc] group-hover:bg-[#4c8dcc] group-hover:text-black">
          <Play className="ml-1 h-5 w-5 fill-current" />
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6">
        <Quote className="mb-3 h-6 w-6 -translate-y-2 text-accent-ink opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100" />
        {quote ? (
          <p className="mb-4 max-h-0 overflow-hidden font-display text-lg italic leading-snug text-tone-200 opacity-0 transition-all duration-500 group-hover:max-h-32 group-hover:opacity-100">
            "{quote}"
          </p>
        ) : null}
        <div className="font-display text-xl text-ink">{speakerDisplayName(speaker)}</div>
        <div className="mt-1 font-body text-[10px] uppercase tracking-[0.2em] text-tone-400">
          {role}
        </div>
        <div className="mt-3 inline-flex items-center gap-2 font-body text-[10px] uppercase tracking-[0.2em] text-accent-ink opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          View full profile
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  );
}

function SpeakersSection({ speakers, status }) {
  const section = useHomeContent('homeSpeakers');
  const { fadeUp, stagger, item } = useMotionPrefs();
  return (
    <section id="speakers" data-testid="home-speakers" className="relative overflow-hidden bg-canvas py-24 md:py-32 lg:py-40">
      <GridGlow />
      <div className="site-content-shell">
        <motion.div className="mb-14 md:mb-16" {...fadeUp}>
          <HomeSectionLabel index="03">{section.eyebrow || 'Course speakers'}</HomeSectionLabel>
          <h2 className="mt-5 max-w-4xl font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-ink leading-[0.95]">
            {section.title || 'Meet the people teaching the work.'}
          </h2>
          <p className="mt-6 max-w-2xl text-tone-400 leading-relaxed">
            {section.body ?? 'Read each speaker\'s background and teaching topics to see how their experience relates to the responsibilities you want to learn.'}
          </p>
        </motion.div>
        {speakers.length ? (
          <motion.div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8" {...stagger}>
            {speakers.slice(0, 3).map((speaker, index) => (
              <motion.div key={speakerKey(speaker) || index} {...item}>
                <SpeakerCard speaker={speaker} index={index} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="rounded-xl border border-ink/10 bg-ink/[0.03] p-7">
            <div className="flex items-start gap-4">
              <UserRound className="mt-1 h-5 w-5 flex-shrink-0 text-accent-ink" />
              <div>
                <h3 className="font-display text-2xl text-ink">Speaker profiles are being prepared.</h3>
                <p className="mt-3 max-w-2xl font-body text-sm leading-7 text-copy/48">
                  {status === 'error' ? 'Speaker profiles are temporarily unavailable. Please try again later.' : status === 'ready' ? 'New speaker profiles will be added as training sessions are scheduled.' : 'Loading upcoming speaker profiles.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function SelectedWorkSection({ posts, status, onRetry }) {
  const { fadeUp, stagger, item } = useMotionPrefs();
  const visiblePosts = posts.slice(0, 4);
  return (
    <section id="selected-work" data-testid="home-selected-work" className="relative bg-canvas py-24 md:py-32 lg:py-40">
      <div className="site-content-shell">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <motion.div className="max-w-3xl" {...fadeUp}>
            <HomeSectionLabel index="05">Blog</HomeSectionLabel>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-ink leading-[0.95]">
              Take a closer look at the issue in front of you.
            </h2>
            <p className="mt-6 max-w-xl text-tone-400 leading-relaxed">
              Explore practical articles on driver records, Safety, dispatch, recruiting and management. Each examines a specific question, from a missing onboarding record to the information needed at a shift handoff.
            </p>
          </motion.div>
          <Link to="/blog" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-tone-400 hover:text-accent-ink transition-colors">
            Explore the Blog <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {visiblePosts.length ? (
          <motion.div className="grid auto-rows-[280px] grid-cols-1 gap-5 md:grid-cols-3 lg:gap-6" {...stagger}>
            {visiblePosts.map((post, index) => (
              <motion.article data-theme-surface="dark"
                key={post.slug}
                className={`home-spotlight group relative overflow-hidden rounded-xl border border-ink/10 ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                {...item}
              >
                <Link to={`/blog/${post.slug}`} className="absolute inset-0">
                  {post.featuredImageUrl ? (
                    <img
                      src={post.featuredImageUrl}
                      alt={post.featuredImageAlt || post.title}
                      className="absolute inset-0 h-full w-full object-cover opacity-62 transition-all duration-1000 ease-out group-hover:scale-110 group-hover:opacity-82"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-gold/18 via-white/[0.04] to-black" />
                  )}
	                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
	                  <div className="relative z-10 flex h-full flex-col justify-between p-7">
                    <div className="flex items-center justify-between gap-4">
                      <span className="rounded-full border border-gold/30 px-3 py-1 font-body text-[10px] uppercase tracking-[0.24em] text-accent-ink">
                        {post.category || 'Blog'}
                      </span>
                      <ArrowUpRight className="h-5 w-5 text-copy/40 transition-all duration-500 group-hover:rotate-45 group-hover:text-ink" />
                    </div>
	                    <div>
	                      <h3 className={`font-display leading-tight text-ink ${index === 0 ? 'text-3xl lg:text-5xl' : 'text-2xl'}`}>
	                        {post.title}
	                      </h3>
                        <p className="mt-3 max-w-xl font-body text-sm leading-6 text-tone-300">
                          {post.excerpt || post.summary || formatBlogDate(post.publishedAt || post.updatedAt) || post.authorName || 'Magellan'}
                        </p>
	                    </div>
	                  </div>
                </Link>
              </motion.article>
            ))}
          </motion.div>
        ) : (
          <div className="rounded-xl border border-ink/10 bg-ink/[0.03] p-7">
            <BookOpen className="mb-5 h-6 w-6 text-accent-ink" />
            <h3 className="font-display text-2xl text-ink">{status === 'error' ? 'Articles are temporarily unavailable.' : status === 'loading' ? 'Loading articles…' : 'New field notes are being prepared.'}</h3>
            <p className="mt-3 font-body text-sm leading-7 text-copy/48">{status === 'error' ? 'The articles could not load. Please try again.' : 'Articles on trucking operations, training, compliance, recruiting and systems.'}</p>
            {status === 'error' && <FlowButton as="button" type="button" onClick={onRetry} className={`${BTN_GHOST} mt-5`}>Retry articles</FlowButton>}
          </div>
        )}
      </div>
    </section>
  );
}

// Package prices come from the owner's saved prices (Ivan 2026-10-15): while a sale is on, the original price is shown
// crossed out with a red line above the price buyers pay. Missing prices use a plain availability label.
const RED_LINE = { textDecorationLine: 'line-through', textDecorationColor: '#dc2626', textDecorationThickness: '2px' };
function PackagePrice({ label, price, loading }) {
  if (!price && loading) return <span role="status" aria-label="Loading package price" className="inline-block h-8 w-20 animate-pulse rounded bg-ink/5 align-middle" />;
  if (!price) return <span className="font-body text-xs font-normal leading-5 text-copy/50">Price unavailable</span>;
  return <>
    {price.onSale && <s className="block font-body text-sm text-copy/48" style={RED_LINE}>{label ? label + ' ' : ''}{formatCartMoney(price.originalMinor)}</s>}
    {formatCartMoney(price.priceMinor)}
  </>;
}

function PaperworkPackagesSection() {
  const prices = usePackagePriceDetails();
  const priceStatus = usePackagePriceStatus();
  const { fadeUp } = useMotionPrefs();
  const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const update = () => setDesktop(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);
  const plans = PAPERWORK_PACKAGES.map(pkg => ({
    id: pkg.id, name: pkg.title, href: pkg.anchor, actionLabel: pkg.ctaLabel,
    description: pkg.line, items: pkg.items, documentCount: pkg.documentCount,
    price: <PackagePrice label={pkg.saleLabel} price={prices[pkg.id]} loading={priceStatus === 'idle' || priceStatus === 'loading'} />,
  }));
  // Document totals confirmed by Ivan on 2026-10-16. Prices still come from the database.
  const allPackages = value => Object.fromEntries(PAPERWORK_PACKAGES.map(pkg => [pkg.id, value]));
  const groups = [
    { id: 'contents', label: 'Package contents', features: [
      { id: 'documents', label: 'Documents included', values: Object.fromEntries(PAPERWORK_PACKAGES.map(pkg => [pkg.id, pkg.documentCount])) },
      { id: 'policies', label: 'Company policies', values: allPackages(true) },
      { id: 'forms', label: 'Forms and operating records', values: allPackages(true) },
      { id: 'workbooks', label: 'Tracking workbooks', values: allPackages(true) },
    ] },
    { id: 'coverage', label: 'Documentation and support', features: [
      ...['Documentation', 'Tools and follow-up', 'Guidance and training'].map((label, index) => ({
        id: `coverage-${index}`, label, values: Object.fromEntries(PAPERWORK_PACKAGES.map(pkg => [pkg.id, pkg.items[index]])),
      })),
    ] },
    { id: 'full-scope', label: 'Full package scope', features: [
      { id: 'scope', label: 'Operating scope', detail: true, values: Object.fromEntries(PAPERWORK_PACKAGES.map(pkg => [pkg.id, pkg.line])) },
      { id: 'setup', label: 'Written setup guidance', detail: true, values: allPackages(true) },
      { id: 'arrangement', label: 'Working arrangement', detail: true, values: allPackages('W-2 or owner-operator; both means two packages') },
      { id: 'edition', label: 'Equipment and state edition', detail: true, values: allPackages('One equipment type and one state per package') },
    ] },
  ];


  return (
    <section id="paperwork-packages" data-testid="home-paperwork-packages" className="relative overflow-hidden border-y border-ink/10 bg-surface py-24 md:py-32">
      <GridGlow accent="#4c8dcc" />
      <div className="site-content-shell">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <motion.div className="max-w-3xl" {...fadeUp}>
            <HomeSectionLabel index="01">Paperwork Packages</HomeSectionLabel>
            <h2 className="mt-5 font-serif font-light text-4xl tracking-tighter text-ink sm:text-5xl lg:text-6xl leading-[0.95]">
              Put the company&apos;s working procedures in writing.
            </h2>
            <p className="mt-6 max-w-2xl font-body text-tone-400 leading-relaxed">
              Compare Basic, Standard and Premium for your operation. Every package includes a review of your existing paperwork after confirmed payment, written findings and a meeting. One package covers one working arrangement, equipment type and state. Choosing both arrangements buys two separate packages at twice the full package price.
            </p>
          </motion.div>
          <Link to="/paperwork?view=compare" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-tone-400 transition-colors hover:text-accent-ink">
            Compare paperwork packages <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <PageStoryImage testId="home-paperwork-image" src="/images/careers/document-review.jpg" alt="Reviewing company paperwork and a checklist beside a laptop" position="center 63%" />
        {desktop ? <SpotlightComparisonTable plans={plans} groups={groups} /> : <PaperworkPackageCarousel plans={plans} />}
      </div>
    </section>
  );
}

function TmsPlatformSection() {
  return <TmsFeatureSection eyebrow={<HomeSectionLabel index="02">CarrierFlow TMS</HomeSectionLabel>} />;
}

function WebinarPreviewModal({ webinar, onClose }) {
  if (!webinar) return null;
  if (typeof document === 'undefined') return null;
  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[70] grid place-items-center overflow-y-auto bg-black/88 p-3 backdrop-blur-md sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          data-theme-surface="dark" className="relative my-auto w-full max-w-5xl border border-ink/10 bg-black/70 p-3 shadow-[0_30px_120px_rgba(0,0,0,0.55)] sm:p-4"
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ duration: 0.35, ease: EASE }}
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" onClick={onClose} className="mb-3 ml-auto flex items-center gap-2 font-body text-xs uppercase tracking-widest text-copy/70 hover:text-ink">
            Close <X className="h-4 w-4" />
          </button>
          <div data-theme-surface="dark" className="aspect-video w-full overflow-hidden rounded-xl border border-ink/10 bg-black">
            {webinar.previewVideoProvider === 'upload' && webinar.previewVideoUrl ? (
              <video
                title={webinar.previewVideoTitle || webinar.title}
                src={webinar.previewVideoUrl}
                className="h-full w-full bg-black object-cover"
                controls
                controlsList="nodownload"
                preload="metadata"
                playsInline
                poster={webinar.previewCoverImageUrl || undefined}
              />
            ) : (
              <iframe
                title={webinar.previewVideoTitle || webinar.title}
                src={webinar.previewYoutubeEmbedUrl}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            )}
          </div>
          <div className="mt-4 flex flex-col gap-2 md:flex-row md:items-baseline md:justify-between">
            <span className="font-display text-2xl text-ink">{webinar.previewVideoTitle || webinar.title}</span>
            <span className="font-body text-[10px] uppercase tracking-[0.22em] text-tone-400">{formatWebinarSchedule(webinar)}</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}

function MasterclassSection({ webinars, status }) {
  const section = useHomeContent('homeMasterclass');
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1);
  const [preview, setPreview] = useState(null);
  const { fadeUp } = useMotionPrefs();
  const sessions = webinars.slice(0, 6);
  const activeWebinar = sessions[active] || null;
  const activeTheme = resolveCoursePresentation(activeWebinar);

  useEffect(() => {
    if (active >= sessions.length) setActive(0);
  }, [active, sessions.length]);

  const go = (direction) => {
    if (!sessions.length) return;
    setDirection(direction);
    setActive((current) => (current + direction + sessions.length) % sessions.length);
  };

  return (
    <section
      id="masterclass"
      data-testid="home-masterclass"
      data-course-presentation="panels"
      data-theme={activeTheme.key}
      className="relative overflow-hidden border-y border-ink/10 py-24 md:py-32 lg:py-40 grain"
      style={{ '--course-light-panel': activeTheme.lightPanelBg, background: `linear-gradient(135deg, var(--editorial-page, ${activeTheme.sectionBg}) 0%, var(--editorial-panel, #0a0a0a) 54%, ${hexToRgba(activeTheme.accent, 0.08)} 100%)` }}
    >
      <GridGlow accent={activeTheme.accent} />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${hexToRgba(activeTheme.accent, 0.5)}, transparent)` }} />
      <div className="site-content-shell">
        <div className="mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <motion.div className="max-w-3xl" {...fadeUp}>
            <HomeSectionLabel index="04" accentColor={`color-mix(in srgb, ${activeTheme.accent} var(--accent-strength, 100%), black)`}>{section.eyebrow || 'Courses'}</HomeSectionLabel>
            <h2 className="mt-5 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-ink leading-[0.95]">
              {section.title || 'Learn the office work behind every load.'}
            </h2>
            <p className="mt-6 max-w-xl text-tone-400 leading-relaxed">
              {section.body ?? 'Choose a recorded course on driver records, onboarding, Safety, recruiting, dispatch handoffs or management. Each course costs $299 per person; the current ten-course series costs $1,499 per person. Recorded videos and learning material are included. Access lasts three months from receipt of payment.'}
            </p>
          </motion.div>
          {sessions.length ? (
            <div className="flex items-center gap-5">
              <span className="font-body text-sm text-tone-500">
                <span className="text-ink">{String(active + 1).padStart(2, '0')}</span> / {String(sessions.length).padStart(2, '0')}
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={() => go(-1)} aria-label="Previous course" className="grid h-11 w-11 place-items-center rounded-full border text-ink transition-colors" style={{ borderColor: hexToRgba(activeTheme.accent, 0.38) }}>
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => go(1)} aria-label="Next course" className="grid h-11 w-11 place-items-center rounded-full border text-ink transition-colors" style={{ borderColor: hexToRgba(activeTheme.accent, 0.38) }}>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {activeWebinar ? (
          <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={activeWebinar.id || activeWebinar.title}
            custom={direction}
            initial={{ opacity: 0, x: direction * 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -60 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="home-spotlight grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-16"
            style={{
              background: `radial-gradient(circle at 6% 0%, ${hexToRgba(activeTheme.accent, 0.16)}, transparent 34%), linear-gradient(135deg, ${hexToRgba(activeTheme.panelBg, 0.52)}, transparent 62%)`,
              borderColor: hexToRgba(activeTheme.accent, 0.18),
            }}
            data-testid="home-masterclass-themed-card"
          >
            <div className="lg:col-span-6">
              <CourseThumb
                alt={activeWebinar.previewCoverImageUrl ? activeWebinar.previewCoverImageAlt : activeWebinar.featuredImageUrl ? activeWebinar.featuredImageAlt : activeWebinar.heroImageAlt}
                thumb={activeWebinar.previewCoverImageUrl || activeWebinar.featuredImageUrl || activeWebinar.heroImageUrl || WEBINAR_FALLBACK_IMAGE}
                accent={activeTheme.accent}
                label={activeWebinar.previewVideoEnabled ? 'Watch preview' : 'Preview coming soon'}
                onPlay={() => (activeWebinar.previewVideoEnabled && (activeWebinar.previewYoutubeEmbedUrl || activeWebinar.previewVideoUrl) ? setPreview(activeWebinar) : null)}
                data-testid="home-masterclass-thumb"
              />
              <div className="mt-5 flex flex-wrap gap-2.5">
                {[
                  ['Schedule', activeWebinar.isDemo ? 'Demo outline' : formatWebinarSchedule(activeWebinar), Clock],
                  ['Payment', activeWebinar.enrollmentEnabled ? activeWebinar.offer?.mode === 'free' ? 'Free registration' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(activeWebinar.offer.priceMinor / 100) : 'Enrollment not enabled', FileText],
                  ['Format', 'Course session', Video],
                ].map(([label, value, Icon]) => (
                  <span key={label} className="inline-flex items-center gap-2 rounded-full border px-4 py-2 font-body text-[10px] uppercase tracking-[0.16em] text-tone-300" style={{ borderColor: hexToRgba(activeTheme.accent, 0.28), backgroundColor: hexToRgba(activeTheme.accent, 0.06) }}>
                    <Icon className="h-3.5 w-3.5" style={{ color: `color-mix(in srgb, ${activeTheme.accent} var(--accent-strength, 100%), black)` }} /> {value}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="mb-4 font-body text-[11px] uppercase tracking-[0.3em]" style={{ color: `color-mix(in srgb, ${activeTheme.accent} var(--accent-strength, 100%), black)` }}>
                {activeTheme.label} {String(active + 1).padStart(2, '0')}
              </div>
              <h3 className="font-display text-4xl font-light leading-[1] tracking-tight text-ink md:text-5xl">
                {activeWebinar.title}
              </h3>
              <p className="mt-5 font-body text-lg leading-8 text-tone-400">
                {activeWebinar.description || activeWebinar.takeaway || "Explore this session's outline and intended audience."}
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <FlowButton to={activeWebinar.primaryCtaPath || getWebinarDetailPath(activeWebinar)} className={`${BTN_PRIMARY} flex-1`}>
                  {activeWebinar.primaryCtaLabel || 'View Course'}
                </FlowButton>
                <FlowButton to={activeWebinar.secondaryCtaPath || "/webinars"} className={`${BTN_GHOST} flex-1`}>
                  {activeWebinar.secondaryCtaLabel || 'Full Series'}
                </FlowButton>
              </div>
              <div className="mt-8 border-t pt-6" style={{ borderColor: hexToRgba(activeTheme.accent, 0.2) }}>
                <div className="mb-4 font-body text-[10px] uppercase tracking-[0.25em] text-tone-500">Topics covered</div>
                <ul className="space-y-3">
                  {(Array.isArray(activeWebinar.covered) && activeWebinar.covered.length ? activeWebinar.covered : [
                    activeWebinar.audience,
                    activeWebinar.takeaway,
                    'What to review in your daily work.',
                  ]).filter(Boolean).slice(0, 5).map((item, index) => (
                    <li key={`${item}-${index}`} className="flex items-start gap-3">
                      <span className="font-body text-xs" style={{ color: hexToRgba(activeTheme.accent, 0.7) }}>{String(index + 1).padStart(2, '0')}</span>
                      <span className="font-display text-lg leading-snug text-tone-200">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
          </AnimatePresence>
        ) : (
          <div className="rounded-xl border border-ink/10 bg-ink/[0.03] p-7">
            <Video className="mb-5 h-6 w-6 text-accent-ink" />
            <h3 className="font-display text-2xl text-ink">New training sessions are being prepared.</h3>
            <p className="mt-3 font-body text-sm leading-7 text-copy/48">
              {status === 'loading' ? 'Loading training sessions.' : status === 'error' ? 'Training sessions are temporarily unavailable. Please try again later.' : 'Check back soon or use the courses page to register interest.'}
            </p>
          </div>
        )}
      </div>
      <WebinarPreviewModal webinar={preview} onClose={() => setPreview(null)} />
    </section>
  );
}

function WorkAreasSection() {
  const { stagger, item } = useMotionPrefs();
  const state = useServices('homepage');
  const heading = useHomeContent('homePathways');
  const areas = (state.data || []).map((entry,index) => ({ title: entry.title, tag: entry.cardLabel || 'Services', result: entry.cardResult || entry.description, path: entry.path, image: entry.service?.cardImageUrl || entry.service?.heroImageUrl || ['/images/resources/resources-hero-documents.png','/images/resources/resources-road-truck.png',null,'/images/webinars/webinars-road-truck.png'][index] || null }));
  return (
    <section id="work-areas" data-testid="home-public-pathways" className="bg-canvas py-24 md:py-32">
      <div className="site-content-shell">
        <SectionIntro
          index="( 07 )"
          eyebrow={heading.eyebrow}
          title={heading.title}
          copy={heading.body}
        />
        {!areas.length && <p role="status">{state.loading ? 'Loading services.' : state.error ? state.error.message : 'No services are currently published.'}</p>}
        <motion.div className="grid auto-rows-[250px] grid-cols-1 gap-5 md:grid-cols-3" {...stagger}>
          {areas.map((area, index) => {
            const isHash = area.path.startsWith('#');
            const card = (
              <motion.div data-theme-surface="dark"
                className={`home-spotlight group relative h-full overflow-hidden rounded-xl border border-ink/10 ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                {...item}
              >
                {area.image ? (
                  <img src={area.image} alt={area.title} className="absolute inset-0 h-full w-full object-cover opacity-58 transition-transform duration-1000 group-hover:scale-110" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gold/22 via-white/[0.04] to-black" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
                <div className="relative z-10 flex h-full flex-col justify-between p-7">
                  <div className="flex items-center justify-between">
                    <span className="rounded-full border border-gold/30 px-3 py-1 font-body text-[10px] uppercase tracking-[0.24em] text-accent-ink">
                      {area.tag}
                    </span>
                    <ArrowUpRight className="h-5 w-5 text-copy/40 transition-all duration-500 group-hover:rotate-45 group-hover:text-ink" />
                  </div>
                  <div>
                    <h3 className={`${index === 0 ? 'text-4xl lg:text-5xl' : 'text-2xl'} font-display leading-tight text-ink`}>
                      {area.title}
                    </h3>
                    <p className="mt-3 font-body text-sm text-tone-300">{area.result}</p>
                  </div>
                </div>
              </motion.div>
            );
            return isHash ? (
              <button key={area.title} type="button" onClick={() => scrollToSection(area.path.slice(1))} className="text-left">
                {card}
              </button>
            ) : (
              <Link key={area.title} to={area.path}>{card}</Link>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function ReserveSection() {
  useHomeContactAnchor();
  const { fadeUp } = useMotionPrefs();
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    dotNumber: '',
    mcNumber: '',
    company: '',
    subject: 'Website contact',
    message: '',
  });
  const [submittedLead, setSubmittedLead] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [identifierError, setIdentifierError] = useState(false);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    if ((field === 'dotNumber' || field === 'mcNumber') && value.trim()) {
      setIdentifierError(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const contactDetails = {
      ...form,
      phone: form.phone.trim(),
      dotNumber: form.dotNumber.trim(),
      mcNumber: form.mcNumber.trim(),
    };
    if (!contactDetails.phone) {
      toast.error('Please provide your phone number.');
      event.currentTarget.querySelector('#home-contact-phone')?.focus();
      return;
    }
    if (!contactDetails.dotNumber && !contactDetails.mcNumber) {
      setIdentifierError(true);
      event.currentTarget.querySelector('#home-contact-dot-number')?.focus();
      return;
    }
    setIdentifierError(false);
    setIsSubmitting(true);

    try {
      const result = await submitWebsiteContact({
        ...contactDetails,
        sourcePage: 'Homepage contact section',
        subject: form.subject || 'Website contact',
      });

      if (!result.success) {
        toast.error('Failed to send your message. Please try again.');
        return;
      }

      const savedLead = {
        referenceId: extractCarrierFlowReference(result.data),
        companyName: form.company || form.name || 'Homepage contact request',
        contactName: form.name,
        email: form.email,
        phone: contactDetails.phone,
        subject: form.subject || 'Website contact',
      };

      setSubmittedLead(savedLead);
      toast.success('Your message has been received.');
    } catch (error) {
      console.error('Homepage contact request failed:', error);
      toast.error('An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section data-theme-surface="dark" id="reserve" data-testid="home-reserve" className="relative overflow-hidden bg-canvas py-24 md:py-32">
      <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${RESERVE_IMAGE})` }} aria-hidden="true" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(16,20,24,0.48),rgba(16,20,24,0.96)_72%)]" />
      <div className="site-content-shell relative z-10">
        <SectionIntro
          index="( 08 )"
          eyebrow="Contact"
          title={<>What would help your company most?</>}
          copy="Tell us what you need: paperwork, training, office staff, monthly reviews or CarrierFlow TMS. Describe the task or problem and how your team handles it today, so we can discuss a suitable scope."
          align="center"
        />
        <motion.div id="contact" tabIndex={-1} role="region" aria-label="Contact info" className="glass w-full rounded-2xl p-6 md:p-10" {...fadeUp}>
            {submittedLead ? (
              <div data-testid="home-reserve-contact-success" className="flex min-h-[420px] flex-col justify-center">
                <div className="mb-6 flex h-12 w-12 items-center justify-center border border-gold/25 bg-gold/[0.08] text-accent-ink">
                  <CheckCircle2 className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-4xl leading-tight text-ink">Contact information received.</h3>
                <p className="mt-5 max-w-xl font-body text-sm leading-7 text-copy/52">
                  Your message about {submittedLead.companyName} has been received. We can follow up using your contact details.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmittedLead(null)}
                  className="mt-7 w-fit border border-ink/15 px-5 py-3 font-body text-[10px] uppercase tracking-[0.22em] text-copy/60 transition-colors hover:border-gold/45 hover:text-accent-ink"
                >
                  Edit contact info
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="home-reserve-contact-form">
                <div>
                  <p className="mb-3 font-body text-[10px] uppercase tracking-[0.28em] text-accent-ink/80">Contact info</p>
                  <h3 className="font-display text-3xl leading-tight text-ink">Who should we contact about your company?</h3>
                  <p className="mt-3 font-body text-sm leading-7 text-copy/45">
                    Submitting this form sends your request. It does not confirm a meeting or payment.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="group">
                    <label htmlFor="home-contact-name" className="mb-3 block font-body text-[10px] uppercase tracking-[0.25em] text-copy/30">Full Name</label>
                    <input id="home-contact-name" type="text" required autoComplete="name" value={form.name} onChange={(event) => updateForm('name', event.target.value)} className="w-full border-b border-ink/10 bg-transparent px-0 py-3 font-body text-sm text-ink outline-none transition-colors placeholder:text-ink/15 focus:border-gold group-hover:border-ink/20" placeholder="Your name" />
                  </div>
                  <div className="group">
                    <label htmlFor="home-contact-email" className="mb-3 block font-body text-[10px] uppercase tracking-[0.25em] text-copy/30">Email</label>
                    <input id="home-contact-email" type="email" required autoComplete="email" value={form.email} onChange={(event) => updateForm('email', event.target.value)} className="w-full border-b border-ink/10 bg-transparent px-0 py-3 font-body text-sm text-ink outline-none transition-colors placeholder:text-ink/15 focus:border-gold group-hover:border-ink/20" placeholder="you@email.com" />
                  </div>
                  <div className="group">
                    <label htmlFor="home-contact-phone" className="mb-3 block font-body text-[10px] uppercase tracking-[0.25em] text-copy/30">Phone (required)</label>
                    <input id="home-contact-phone" type="tel" required autoComplete="tel" value={form.phone} onChange={(event) => updateForm('phone', event.target.value)} className="w-full border-b border-ink/10 bg-transparent px-0 py-3 font-body text-sm text-ink outline-none transition-colors placeholder:text-ink/15 focus:border-gold group-hover:border-ink/20" placeholder="Phone number" />
                  </div>
                  <div className="group">
                    <label htmlFor="home-contact-company" className="mb-3 block font-body text-[10px] uppercase tracking-[0.25em] text-copy/30">Company</label>
                    <input id="home-contact-company" type="text" autoComplete="organization" value={form.company} onChange={(event) => updateForm('company', event.target.value)} className="w-full border-b border-ink/10 bg-transparent px-0 py-3 font-body text-sm text-ink outline-none transition-colors placeholder:text-ink/15 focus:border-gold group-hover:border-ink/20" placeholder="Company name" />
                  </div>
                  <div className="group">
                    <label htmlFor="home-contact-dot-number" className="mb-3 block font-body text-[10px] uppercase tracking-[0.25em] text-copy/30">DOT number</label>
                    <input id="home-contact-dot-number" type="text" value={form.dotNumber} onChange={(event) => updateForm('dotNumber', event.target.value)} aria-describedby="home-contact-carrier-hint" aria-invalid={identifierError || undefined} className="w-full border-b border-ink/10 bg-transparent px-0 py-3 font-body text-sm text-ink outline-none transition-colors placeholder:text-ink/15 focus:border-gold group-hover:border-ink/20" placeholder="DOT number" />
                  </div>
                  <div className="group">
                    <label htmlFor="home-contact-mc-number" className="mb-3 block font-body text-[10px] uppercase tracking-[0.25em] text-copy/30">MC number</label>
                    <input id="home-contact-mc-number" type="text" value={form.mcNumber} onChange={(event) => updateForm('mcNumber', event.target.value)} aria-describedby="home-contact-carrier-hint" aria-invalid={identifierError || undefined} className="w-full border-b border-ink/10 bg-transparent px-0 py-3 font-body text-sm text-ink outline-none transition-colors placeholder:text-ink/15 focus:border-gold group-hover:border-ink/20" placeholder="MC number" />
                  </div>
                  <p id="home-contact-carrier-hint" role={identifierError ? 'alert' : undefined} className={`font-body text-sm md:col-span-2 ${identifierError ? 'text-accent-ink' : 'text-copy/45'}`}>Provide at least one: DOT or MC number.</p>
                </div>
                <div className="group">
                  <label htmlFor="home-contact-message" className="mb-3 block font-body text-[10px] uppercase tracking-[0.25em] text-copy/30">What needs attention?</label>
                  <textarea id="home-contact-message" rows={5} required value={form.message} onChange={(event) => updateForm('message', event.target.value)} className="w-full resize-none border-b border-ink/10 bg-transparent px-0 py-3 font-body text-sm text-ink outline-none transition-colors placeholder:text-ink/15 focus:border-gold group-hover:border-ink/20" placeholder="Describe the task or problem, who handles it and where the work gets delayed." />
                </div>
                <FlowButton as="button" type="submit" disabled={isSubmitting} className={BTN_PRIMARY}>
                  {isSubmitting ? (
                    <>Sending… <Loader2 className="h-4 w-4 animate-spin" /></>
                  ) : (
                    <>Send Message</>
                  )}
                </FlowButton>
              </form>
            )}
        </motion.div>
      </div>
    </section>
  );
}

function FaqSection() {
  const { stagger, item } = useMotionPrefs();
  return (
    <section id="faq" data-testid="home-faq" className="border-t border-ink/10 bg-canvas py-24 md:py-32">
      <div className="site-content-shell">
        <SectionIntro
          index="( 09 )"
          eyebrow="Questions"
          title={<>Questions before you begin.</>}
          align="center"
        />
        <motion.div {...stagger}>
          <Accordion type="single" collapsible className="w-full border-t border-ink/10" data-testid="faq-accordion">
            {FAQS.map((faq, index) => (
              <motion.div key={faq.q} {...item}>
                <AccordionItem value={`faq-${index}`} data-testid={`faq-item-${index}`} className="border-b border-ink/10">
                  <AccordionTrigger className="py-7 text-left hover:no-underline group">
                    <div className="flex items-start gap-5">
                      <span className="font-display text-2xl text-tone-600 transition-colors group-hover:text-accent-ink">{String(index + 1).padStart(2, '0')}</span>
                      <span className="font-display text-xl text-ink transition-colors group-hover:text-accent-ink md:text-2xl">{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-7 pl-[3.4rem] font-body text-base leading-8 text-tone-400">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const interactionRoot = useRef(null);
  useHomeSpotlights(interactionRoot);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogStatus, setBlogStatus] = useState('loading');
  const [blogAttempt, setBlogAttempt] = useState(0);
  const [webinars, setWebinars] = useState([]);
  const [webinarStatus, setWebinarStatus] = useState('loading');
  const [speakerProfiles, setSpeakerProfiles] = useState([]);
  const [speakerStatus, setSpeakerStatus] = useState('loading');

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || window.location.hash) {
      return undefined;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const timer = window.setTimeout(() => {
      if (!window.location.hash) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 180);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let mounted = true;
    setBlogStatus('loading');
    listPublishedBlogPosts().then((posts) => {
      if (mounted) {
        setBlogPosts(Array.isArray(posts) ? posts : []);
        setBlogStatus('ready');
      }
    }).catch(() => {
      if (mounted) setBlogStatus('error');
    });
    return () => {
      mounted = false;
    };
  }, [blogAttempt]);

  useEffect(() => {
    const controller = new AbortController();
    listPublicCourses(controller.signal).then(records => {
      if (!controller.signal.aborted) { setWebinars(records); setWebinarStatus('ready'); }
    }).catch(() => { if (!controller.signal.aborted) { setWebinars([]); setWebinarStatus('error'); } });
    listPublicSpeakers(controller.signal).then(records => {
      if (!controller.signal.aborted) { setSpeakerProfiles(records); setSpeakerStatus('ready'); }
    }).catch(() => { if (!controller.signal.aborted) { setSpeakerProfiles([]); setSpeakerStatus('error'); } });
    return () => controller.abort();
  }, []);
  const publicWebinars = useMemo(() => webinars.filter(record => record.homepageVisible === true).sort(contentOrder('homepageOrder')), [webinars]);
  const speakers = useMemo(() => speakerProfiles.filter(record => record.homepageVisible === true).sort(contentOrder('homepageOrder')), [speakerProfiles]);

  return (
    <div ref={interactionRoot} className="min-h-screen bg-canvas">
      <HomeHero />
      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>
      <HomeIntroBand />
      <HomeOperatingTextSection />
      <PaperworkPackagesSection />
      <TmsPlatformSection />
      <SpeakersSection speakers={speakers} status={speakerStatus} />
      <MasterclassSection webinars={publicWebinars} status={webinarStatus} />
      <SelectedWorkSection posts={blogPosts} status={blogStatus} onRetry={() => setBlogAttempt(attempt => attempt + 1)} />
      <HomeTestimonialsSection />
      <WorkAreasSection />
      <ReserveSection />
      <FaqSection />
      <section data-testid="home-closing-cta" className="border-t border-ink/10 bg-canvas px-6 py-20 text-center md:px-12 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="mb-8 flex items-center justify-center gap-4">
            <span className="h-px w-14 bg-gold/35" />
            <span className="h-2 w-2 rotate-45 border border-gold/60" />
            <span className="h-px w-14 bg-gold/35" />
          </div>
          <HomeSectionLabel index="10" className="mb-5 justify-center">Next step</HomeSectionLabel>
          <h2 className="font-display text-5xl font-light leading-[0.95] tracking-tight text-ink md:text-6xl">
            Give yourself time to manage the company.
          </h2>
          <div className="mt-10 flex flex-col justify-center gap-3 sm:flex-row">
            <FlowButton to="/paperwork#paperwork-hero" data-testid="home-closing-cta-primary" className={BTN_PRIMARY}>
              View paperwork packages
            </FlowButton>
            <FlowButton to="/tms" data-testid="home-closing-cta-secondary" className={BTN_GHOST}>
              Explore CarrierFlow TMS
            </FlowButton>
          </div>
        </div>
      </section>
    </div>
  );
}
